import React, { useState, useEffect, useRef } from "react";
import SEOHead from "../components/SEOHead";
import { motion, AnimatePresence } from "framer-motion";
import { format, addDays, startOfDay } from "date-fns";
import {
  Calendar,
  Clock,
  Loader2,
  CheckCircle,
  User,
  Mail,
  ChevronRight,
  ArrowLeft,
  Pencil,
  Check,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { PhoneInputEmpire, getFullPhoneNumber } from "../components/PhoneInputEmpire";
import { sanitizeName } from "../lib/inputSanitizers";
import { recaptchaHeaders } from "../lib/recaptcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_STUDIO_VIDEO = "https://empire-dance-media.sgp1.cdn.digitaloceanspaces.com/empire-dance-media/general/3a910e0ae2c24898a30ee4086ebabe58.mp4";
const DEFAULT_STUDIO_IMAGES = [
  "https://images.unsplash.com/photo-1679205691593-bd4a01e2108b?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1600258675559-70c2a6cd2b7f?crop=entropy&cs=srgb&fm=jpg&q=85",
];

const FEATURES = [
  "Mirrors + Sprung Floor",
  "Sound System + Aux",
  "AC + Private Changing",
  "High Speed WiFi",
  "LED Lighting",
];

const PURPOSE_OPTIONS = [
  "Rehearsal",
  "Photo Shoot",
  "Private Practice",
  "Corporate / Event",
  "Other",
];

const fmtHour = (h) => {
  const period = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:00 ${period}`;
};

const getTimeGroup = (hour) => {
  if (hour >= 6 && hour <= 11) return "Morning";
  if (hour >= 12 && hour <= 16) return "Afternoon";
  return "Evening";
};

export const StudioBookingPage = () => {
  const [step, setStep] = useState(0); // 0=landing, 1=time, 2=details, 3=success
  const [submitting, setSubmitting] = useState(false);

  // Fixed date list — today + 2 days ahead
  const [dates] = useState(() =>
    [0, 1, 2].map((i) => addDays(startOfDay(new Date()), i))
  );

  // Slots per day — { "2026-03-20": [{start_time, end_time, display}, ...] }
  const [slotsPerDay, setSlotsPerDay] = useState({});
  const [loadingDays, setLoadingDays] = useState({});
  const [hourlyRate, setHourlyRate] = useState(2000);

  // Studio media assets
  const [studioVideo, setStudioVideo] = useState(DEFAULT_STUDIO_VIDEO);
  const [studioVideoThumbnail, setStudioVideoThumbnail] = useState(null);
  const [studioImages, setStudioImages] = useState(DEFAULT_STUDIO_IMAGES);

  // Which day tab is active
  const [activeTab, setActiveTab] = useState(0);

  // Selection
  const [selectedDateStr, setSelectedDateStr] = useState(null);
  const [selectedStartHour, setSelectedStartHour] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);

  // Guest info
  const [guestInfo, setGuestInfo] = useState({
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    purpose: "",
    purposeDetail: "",
    notes: "",
  });

  // Booking result
  const [bookingResult, setBookingResult] = useState(null);

  const [videoLoaded, setVideoLoaded] = useState(false);

  // Preload video by creating a hidden video element
  const preloadVideo = (url) => {
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.src = url;
  };

  // Preload default media immediately on mount
  useEffect(() => {
    preloadVideo(DEFAULT_STUDIO_VIDEO);
    DEFAULT_STUDIO_IMAGES.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  }, []);

  // Fetch studio settings (rate + media)
  useEffect(() => {
    const fetchStudioSettings = async () => {
      try {
        const res = await axios.get(`${API}/studio-settings/public`);
        if (res.data.hourly_rate) setHourlyRate(res.data.hourly_rate);
        
        const videoUrl = res.data.media?.video || DEFAULT_STUDIO_VIDEO;
        const thumbnailUrl = res.data.media?.video_thumbnail || null;
        const imageUrls = res.data.media?.images?.length > 0 
          ? res.data.media.images 
          : DEFAULT_STUDIO_IMAGES;
        
        setStudioVideo(videoUrl);
        setStudioVideoThumbnail(thumbnailUrl);
        setStudioImages(imageUrls);

        // Preload thumbnail immediately (it's small and shows first)
        if (thumbnailUrl) {
          const img = new Image();
          img.src = thumbnailUrl;
        }

        // Preload custom video if different from default
        if (videoUrl !== DEFAULT_STUDIO_VIDEO) {
          preloadVideo(videoUrl);
        }
        
        // Preload custom images
        imageUrls.forEach((url) => {
          if (url && !DEFAULT_STUDIO_IMAGES.includes(url)) {
            const img = new Image();
            img.src = url;
          }
        });
      } catch {
        // Defaults already preloaded
      }
    };
    fetchStudioSettings();
  }, []);

  // Load all 3 days in parallel on mount
  useEffect(() => {
    const init = {};
    dates.forEach((d) => { init[format(d, "yyyy-MM-dd")] = true; });
    setLoadingDays(init);

    dates.forEach(async (date) => {
      const key = format(date, "yyyy-MM-dd");
      try {
        const res = await axios.get(`${API}/studio-booking/available-slots?date=${key}`);
        setSlotsPerDay((prev) => ({ ...prev, [key]: res.data.available_slots || [] }));
        if (res.data.hourly_rate) setHourlyRate(res.data.hourly_rate);
      } catch {
        setSlotsPerDay((prev) => ({ ...prev, [key]: [] }));
      } finally {
        setLoadingDays((prev) => ({ ...prev, [key]: false }));
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear selection when changing tabs
  const handleTabChange = (tabIndex) => {
    setActiveTab(tabIndex);
    // Clear previous selection when switching days
    setSelectedDateStr(null);
    setSelectedStartHour(null);
    setSelectedDuration(null);
  };

  const isHourAvailable = (dateKey, hour) => {
    const slots = slotsPerDay[dateKey] || [];
    return slots.some((s) => parseInt(s.start_time) === hour);
  };

  const getAvailableHours = (dateKey) => {
    const slots = slotsPerDay[dateKey] || [];
    const currentHour = new Date().getHours();
    const isToday = dateKey === format(dates[0], "yyyy-MM-dd");
    
    return slots
      .map((s) => parseInt(s.start_time))
      .filter((h) => !isToday || h > currentHour)
      .sort((a, b) => a - b);
  };

  const getSlotCountForDay = (dateKey) => getAvailableHours(dateKey).length;

  // Max consecutive available hours from a given start
  const getDurationOptions = (dateKey, startHour) => {
    const options = [];
    let h = startHour;
    while (isHourAvailable(dateKey, h) && h + 1 <= 22) {
      options.push(h - startHour + 1);
      h++;
    }
    return options;
  };

  const handleSlotClick = (hour) => {
    const dateKey = format(dates[activeTab], "yyyy-MM-dd");
    if (!isHourAvailable(dateKey, hour)) return;
    
    if (selectedDateStr === dateKey && selectedStartHour === hour) {
      setSelectedStartHour(null);
      setSelectedDuration(null);
      return;
    }
    setSelectedDateStr(dateKey);
    setSelectedStartHour(hour);
    setSelectedDuration(null);
  };

  // Derived values
  const startTimeStr = selectedStartHour !== null
    ? `${String(selectedStartHour).padStart(2, "0")}:00`
    : "";
  const endTimeStr = selectedStartHour !== null && selectedDuration !== null
    ? `${String(selectedStartHour + selectedDuration).padStart(2, "0")}:00`
    : "";

  const calculateTotal = () => (selectedDuration || 0) * hourlyRate;

  const canContinue = selectedDateStr && selectedStartHour !== null && selectedDuration !== null;

  const goToStep = (newStep) => {
    setStep(newStep);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Country code for phone
  const [countryCode, setCountryCode] = useState("+254");
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDateStr || !startTimeStr || !endTimeStr) {
      toast.error("Please select a time slot and duration");
      return;
    }
    if (!guestInfo.guest_name || !guestInfo.guest_email || !guestInfo.guest_phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!isPhoneValid) {
      setPhoneTouched(true);
      toast.error(countryCode === "+254" 
        ? "Invalid Kenyan phone number format" 
        : "Please enter a valid phone number"
      );
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        guest_name: guestInfo.guest_name,
        guest_email: guestInfo.guest_email,
        guest_phone: getFullPhoneNumber(countryCode, guestInfo.guest_phone),
        purpose: guestInfo.purpose === "Other" ? (guestInfo.purposeDetail || "Other") : guestInfo.purpose,
        notes: guestInfo.notes,
        booking_date: selectedDateStr,
        start_time: startTimeStr,
        end_time: endTimeStr,
      };
      const rcHeaders = await recaptchaHeaders("studio_booking");
      const res = await axios.post(`${API}/studio-booking`, payload, { headers: rcHeaders });
      setBookingResult(res.data.booking);
      goToStep(3);
      toast.success("Studio booking submitted successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  const resetBooking = () => {
    goToStep(0);
    setActiveTab(0);
    setSelectedDateStr(null);
    setSelectedStartHour(null);
    setSelectedDuration(null);
    setGuestInfo({ guest_name: "", guest_email: "", guest_phone: "", purpose: "", purposeDetail: "", notes: "" });
    setCountryCode("+254");
    setIsPhoneValid(false);
    setPhoneTouched(false);
    setBookingResult(null);
    // Refresh slot data
    dates.forEach(async (date) => {
      const key = format(date, "yyyy-MM-dd");
      setLoadingDays((prev) => ({ ...prev, [key]: true }));
      try {
        const res = await axios.get(`${API}/studio-booking/available-slots?date=${key}`);
        setSlotsPerDay((prev) => ({ ...prev, [key]: res.data.available_slots || [] }));
      } catch {
        setSlotsPerDay((prev) => ({ ...prev, [key]: [] }));
      } finally {
        setLoadingDays((prev) => ({ ...prev, [key]: false }));
      }
    });
  };

  const getSelectedDateFormatted = () => {
    if (!selectedDateStr) return "";
    const date = dates.find((d) => format(d, "yyyy-MM-dd") === selectedDateStr);
    return date ? format(date, "EEE d MMM") : "";
  };

  const getEndTimeFormatted = () => {
    if (selectedStartHour === null || selectedDuration === null) return "";
    return fmtHour(selectedStartHour + selectedDuration);
  };

  // Mobile slider state
  const [mobileSlideIndex, setMobileSlideIndex] = useState(0);
  const videoRef = useRef(null);
  const totalSlides = 1 + studioImages.slice(0, 2).length; // 1 video + 2 images

  const handleSlideChange = (newIndex) => {
    setMobileSlideIndex(newIndex);
    // Pause video when swiping away from it
    if (videoRef.current) {
      if (newIndex === 0) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  };

  // --- Render ---

    return (
    <div data-testid="studio-booking-page" className="overflow-x-hidden">
      <SEOHead page="studio" />
      <AnimatePresence mode="wait">
        
        {/* Step 0 — Studio Showcase Landing */}
        {step === 0 && (
          <motion.div
            key="landing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Mobile: Single-slide carousel with dots */}
            <div className="md:hidden px-4 pt-2 pb-4">
              <div className="relative overflow-hidden rounded-2xl">
                {/* Slides container */}
                <div 
                  className="flex transition-transform duration-300 ease-out"
                  style={{ transform: `translateX(-${mobileSlideIndex * 100}%)` }}
                >
                  {/* Video slide */}
                  <div className="w-full flex-shrink-0 relative">
                    {!videoLoaded && !studioVideoThumbnail && (
                      <div className="absolute inset-0 bg-neutral-800 animate-pulse flex items-center justify-center rounded-2xl">
                        <Loader2 className="w-6 h-6 text-empire-gold animate-spin" />
              </div>
                    )}
                    <video
                      ref={videoRef}
                      src={studioVideo}
                      poster={studioVideoThumbnail || undefined}
                      className={`w-full h-52 object-cover rounded-2xl transition-opacity duration-300 ${videoLoaded || studioVideoThumbnail ? "opacity-100" : "opacity-0"}`}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="auto"
                      onCanPlay={() => setVideoLoaded(true)}
                    />
                  </div>
                  {/* Image slides */}
                  {studioImages.slice(0, 2).map((src, i) => (
                    <div key={i} className="w-full flex-shrink-0">
                      <img
                        src={src}
                        alt={`Studio view ${i + 1}`}
                        className="w-full h-52 object-cover rounded-2xl"
                      />
                    </div>
                  ))}
                </div>

                {/* Navigation arrows */}
                {mobileSlideIndex > 0 && (
                  <button
                    onClick={() => handleSlideChange(mobileSlideIndex - 1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                )}
                {mobileSlideIndex < totalSlides - 1 && (
                  <button
                    onClick={() => handleSlideChange(mobileSlideIndex + 1)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Dot indicators */}
              <div className="flex justify-center gap-2 mt-3">
                {Array.from({ length: totalSlides }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handleSlideChange(i)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i === mobileSlideIndex 
                        ? "bg-empire-gold w-4" 
                        : "bg-neutral-600"
                    }`}
                  />
                ))}
                  </div>
                  </div>

            <div className="container-empire">
              <div className="flex flex-col md:flex-row md:items-start md:gap-10 lg:gap-14">
                {/* Left: Copy */}
                <motion.div
                  className="md:w-[45%] py-2 md:py-10"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: {},
                    visible: { transition: { staggerChildren: 0.1 } },
                  }}
                >
                  <motion.span
                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                    className="font-cookie text-xl md:text-3xl text-empire-gold block mb-1"
                  >
                    Your space to create
                  </motion.span>
                  
                  <motion.h1
                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                    className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-3"
                  >
                    Book the Studio
                  </motion.h1>
                  
                  <motion.p
                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                    className="text-neutral-400 text-sm md:text-base mb-3 md:mb-4"
                  >
                    Professional space for rehearsals, shoots, and private events.
                  </motion.p>

                  <motion.div
                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                    className="mb-3 md:mb-5"
                  >
                    <span className="text-lg md:text-xl font-bold text-empire-gold">
                      From KES {hourlyRate.toLocaleString()}/hr
                    </span>
                  </motion.div>

                  {/* Feature list with checkmarks */}
                  <motion.ul
                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                    className="space-y-1.5 md:space-y-2 mb-4 md:mb-6"
                  >
                    {FEATURES.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 md:gap-3 text-neutral-300 text-sm md:text-base">
                        <Check className="w-4 h-4 text-empire-gold flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </motion.ul>

                  <motion.div
                    variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                    className="pb-4 md:pb-0"
                  >
                    <Button
                      onClick={() => goToStep(1)}
                      className="btn-empire h-11 md:h-12 px-5 md:px-6 text-sm md:text-base font-semibold w-full md:w-auto"
                    >
                      Check Availability
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </motion.div>
                </motion.div>

                {/* Right: Video + 2 Images (desktop only) */}
                <div className="hidden md:block md:w-[55%] md:py-10">
                  {/* Main video */}
                  <motion.div
                    initial={{ opacity: 0, scale: 1.02 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.15 }}
                    className="mb-3 relative"
                  >
                    {!videoLoaded && !studioVideoThumbnail && (
                      <div className="absolute inset-0 bg-neutral-800 rounded-2xl animate-pulse flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-empire-gold animate-spin" />
                  </div>
                    )}
                    <video
                      src={studioVideo}
                      poster={studioVideoThumbnail || undefined}
                      className={`w-full h-56 lg:h-64 object-cover rounded-2xl transition-opacity duration-300 ${videoLoaded || studioVideoThumbnail ? "opacity-100" : "opacity-0"}`}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="metadata"
                      onCanPlay={() => setVideoLoaded(true)}
                    />
                  </motion.div>
                  {/* 2 Images below */}
                  <div className="grid grid-cols-2 gap-3">
                    <motion.img
                      initial={{ opacity: 0, scale: 1.02 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.25 }}
                      src={studioImages[0] || DEFAULT_STUDIO_IMAGES[0]}
                      alt="Studio view 1"
                      className="w-full h-32 lg:h-36 object-cover rounded-2xl"
                    />
                    <motion.img
                      initial={{ opacity: 0, scale: 1.02 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.35 }}
                      src={studioImages[1] || DEFAULT_STUDIO_IMAGES[1]}
                      alt="Studio view 2"
                      className="w-full h-32 lg:h-36 object-cover rounded-2xl"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
            )}

        {/* Step 1 — Visual Availability Picker */}
        {step === 1 && (
          <motion.div
            key="picker"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`container-empire ${canContinue ? "pb-28" : "pb-6"} md:pb-8`}
          >
            {/* Back link */}
                    <button
              onClick={() => goToStep(0)}
              className="flex items-center gap-2 text-neutral-400 hover:text-empire-gold transition-colors mt-2 md:mt-4 mb-4 md:mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Studio
                    </button>

            <h2 className="text-lg md:text-2xl font-bold text-white mb-3 md:mb-4">
              Select Date & Time
            </h2>

            {/* Main content grid - slots on left, selection summary on right */}
            <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
              {/* Left: Date tabs and time slots */}
              <div className="flex-1">
                {/* Day tabs */}
                <div className="flex gap-2 mb-4">
                  {dates.map((date, i) => {
                    const dateKey = format(date, "yyyy-MM-dd");
                    const slotCount = getSlotCountForDay(dateKey);
                    const isActive = activeTab === i;
                    const isLoading = loadingDays[dateKey];

                    return (
                      <button
                        key={i}
                        onClick={() => handleTabChange(i)}
                        className={`flex-1 px-3 py-2.5 rounded-xl font-semibold transition-all text-center ${
                          isActive
                            ? "bg-empire-gold text-black"
                            : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                        }`}
                      >
                        <span className="block text-sm">
                          {i === 0 ? "Today" : i === 1 ? "Tomorrow" : format(date, "EEE d")}
                        </span>
                        {!isLoading && (
                          <span className={`text-xs ${isActive ? "text-black/70" : "text-empire-gold"}`}>
                            {slotCount} slot{slotCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </button>
                    );
                  })}
                        </div>

                {/* Slots grouped by time of day */}
                {(() => {
                  const dateKey = format(dates[activeTab], "yyyy-MM-dd");
                  const isLoading = loadingDays[dateKey];
                  const availableHours = getAvailableHours(dateKey);

                  if (isLoading) {
                    return (
                      <div className="flex justify-center py-12">
                        <Loader2 className="w-6 h-6 text-empire-gold animate-spin" />
                          </div>
                    );
                  }

                  if (availableHours.length === 0) {
                    return (
                      <div className="text-center py-8 bg-neutral-900/50 rounded-xl">
                        <Calendar className="w-8 h-8 text-neutral-600 mx-auto mb-2" />
                        <p className="text-neutral-400 text-sm">No slots available — try another day</p>
                      </div>
                    );
                  }

                  const groups = {
                    Morning: availableHours.filter((h) => getTimeGroup(h) === "Morning"),
                    Afternoon: availableHours.filter((h) => getTimeGroup(h) === "Afternoon"),
                    Evening: availableHours.filter((h) => getTimeGroup(h) === "Evening"),
                  };

                  return (
                    <div className="space-y-3">
                      {Object.entries(groups).map(([groupName, hours]) => {
                        if (hours.length === 0) return null;
                        return (
                          <div key={groupName}>
                            <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-1.5">
                              {groupName}
                            </h3>
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-4 gap-2">
                              {hours.map((hour) => {
                                const isSelected = selectedDateStr === dateKey && selectedStartHour === hour;
                              return (
                                  <button
                                  key={hour}
                                    onClick={() => handleSlotClick(hour)}
                                    className={`h-11 px-3 rounded-lg font-medium text-sm transition-all ${
                                      isSelected
                                        ? "bg-empire-gold text-black"
                                        : "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                                    }`}
                                >
                                  {fmtHour(hour)}
                                  </button>
                              );
                            })}
                            </div>
                      </div>
                    );
                  })}
                </div>
                  );
                })()}

                {/* Duration chips */}
                <AnimatePresence>
                {selectedDateStr && selectedStartHour !== null && (() => {
                  const options = getDurationOptions(selectedDateStr, selectedStartHour);
                    if (options.length === 0) return null;

                    return (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="mt-4 p-4 bg-neutral-900/50 border border-neutral-800 rounded-xl"
                      >
                        <Label className="text-white text-sm block mb-2">
                          Duration — from {fmtHour(selectedStartHour)}
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {options.map((d) => (
                          <button
                            key={d}
                              onClick={() => setSelectedDuration(d)}
                              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                              selectedDuration === d
                                ? "bg-empire-gold text-black border-empire-gold"
                                  : "bg-neutral-800 text-neutral-300 border-neutral-700 hover:border-empire-gold/60"
                            }`}
                          >
                              {d} hr{d > 1 ? "s" : ""}
                          </button>
                        ))}
                      </div>
                      </motion.div>
                    );
                  })()}
                </AnimatePresence>
                    </div>

              {/* Right: Selection summary (desktop) */}
              <div className="hidden lg:block w-72">
                <div className="sticky top-24 p-5 bg-neutral-900 border border-neutral-800 rounded-2xl">
                  <h3 className="text-white font-semibold mb-4">Your Selection</h3>
                  
                  {!selectedDateStr ? (
                    <p className="text-neutral-500 text-sm">Select a time slot to continue</p>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                      <span className="text-neutral-400">Date</span>
                        <span className="text-white font-medium">{getSelectedDateFormatted()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-400">Time</span>
                        <span className="text-white font-medium">
                          {fmtHour(selectedStartHour)}
                          {selectedDuration && ` – ${getEndTimeFormatted()}`}
                      </span>
                    </div>
                      {selectedDuration && (
                        <>
                          <div className="flex justify-between text-sm">
                      <span className="text-neutral-400">Duration</span>
                            <span className="text-white font-medium">{selectedDuration} hr{selectedDuration > 1 ? "s" : ""}</span>
                    </div>
                          <div className="flex justify-between text-sm">
                      <span className="text-neutral-400">Rate</span>
                      <span className="text-neutral-300">KES {hourlyRate.toLocaleString()}/hr</span>
                    </div>
                          <div className="pt-3 border-t border-neutral-800">
                            <div className="flex justify-between">
                      <span className="text-white font-medium">Total</span>
                              <span className="text-xl font-bold text-empire-gold">
                        KES {calculateTotal().toLocaleString()}
                      </span>
                    </div>
                          </div>
                        </>
                      )}
                  </div>
                )}

                <Button
                    onClick={() => goToStep(2)}
                    disabled={!canContinue}
                    className="w-full btn-empire h-11 mt-5"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Mobile sticky bottom bar */}
            {canContinue && (
              <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 glass border-t border-neutral-800">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-white font-medium text-sm">
                      {getSelectedDateFormatted()} · {fmtHour(selectedStartHour)} – {getEndTimeFormatted()}
                    </p>
                  </div>
                  <span className="text-lg font-bold text-empire-gold">
                    KES {calculateTotal().toLocaleString()}
                  </span>
                </div>
                <Button onClick={() => goToStep(2)} className="btn-empire w-full h-11">
                  Continue
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {/* Step 2 — Details Form */}
        {step === 2 && (
          <motion.div
            key="details"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="container-empire max-w-xl pb-8"
          >
            {/* Back link */}
            <button
              onClick={() => goToStep(1)}
              className="flex items-center gap-2 text-neutral-400 hover:text-empire-gold transition-colors mt-4 mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            {/* Selected slot pill */}
            <div className="flex items-center justify-between p-3 bg-neutral-900 border border-neutral-800 rounded-xl mb-6">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-empire-gold" />
                <span className="text-white text-sm font-medium">
                  {getSelectedDateFormatted()} · {fmtHour(selectedStartHour)} – {getEndTimeFormatted()} · {selectedDuration} hr{selectedDuration > 1 ? "s" : ""} · KES {calculateTotal().toLocaleString()}
                </span>
                      </div>
              <button
                onClick={() => goToStep(1)}
                className="p-1.5 hover:bg-neutral-800 rounded-lg transition-colors"
              >
                <Pencil className="w-4 h-4 text-neutral-400" />
              </button>
                  </div>

            <h2 className="text-xl font-bold text-white mb-5">Your Details</h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
                  <div className="space-y-2">
                    <Label className="text-white">Full Name *</Label>
                    <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                      <Input
                        value={guestInfo.guest_name}
                        onChange={(e) => setGuestInfo({ ...guestInfo, guest_name: sanitizeName(e.target.value) })}
                    className="input-empire pl-11"
                        placeholder="John Doe"
                        required
                      />
                    </div>
                  </div>

              {/* Email */}
                  <div className="space-y-2">
                    <Label className="text-white">Email Address *</Label>
                    <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                      <Input
                        type="email"
                        value={guestInfo.guest_email}
                        onChange={(e) => setGuestInfo({ ...guestInfo, guest_email: e.target.value })}
                    className="input-empire pl-11"
                        placeholder="john@example.com"
                        required
                      />
                    </div>
                  </div>

              {/* Phone */}
                  <div className="space-y-2">
                    <Label className="text-white">Phone Number *</Label>
                <PhoneInputEmpire
                        value={guestInfo.guest_phone}
                  onChange={(val) => {
                    setGuestInfo({ ...guestInfo, guest_phone: val });
                    if (!phoneTouched && val) setPhoneTouched(true);
                  }}
                  countryCode={countryCode}
                  onCountryCodeChange={setCountryCode}
                  onValidChange={setIsPhoneValid}
                  touched={phoneTouched}
                  placeholder="712 345 678"
                />
                {guestInfo.guest_phone && !isPhoneValid && (
                  <p className="text-xs text-red-400">
                    {countryCode === "+254" 
                      ? "Invalid Kenyan phone number format" 
                      : "Enter a valid phone number"}
                  </p>
                )}
                {countryCode === "+254" && !guestInfo.guest_phone && (
                  <p className="text-xs text-neutral-500">
                    This number will receive the M-Pesa payment prompt
                  </p>
                )}
                  </div>

              {/* Purpose — chip selector */}
                  <div className="space-y-2">
                    <Label className="text-white">Purpose of Booking</Label>
                <div className="flex flex-wrap gap-2">
                  {PURPOSE_OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setGuestInfo({ ...guestInfo, purpose: option })}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                        guestInfo.purpose === option
                          ? "bg-empire-gold text-black border-empire-gold"
                          : "bg-neutral-800 text-neutral-300 border-neutral-700 hover:border-neutral-600"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                    </div>
                  </div>

              {/* Other purpose — overwrite purpose with the actual text */}
              <AnimatePresence>
                {guestInfo.purpose === "Other" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-2"
                  >
                    <Label className="text-white">Please specify</Label>
                    <Textarea
                      value={guestInfo.purposeDetail ?? ""}
                      onChange={(e) => setGuestInfo({ ...guestInfo, purposeDetail: e.target.value })}
                      className="input-empire min-h-[70px]"
                      placeholder="Tell us more about your booking..."
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Notes */}
                  <div className="space-y-2">
                    <Label className="text-white">Additional Notes</Label>
                    <Textarea
                      value={guestInfo.notes}
                      onChange={(e) => setGuestInfo({ ...guestInfo, notes: e.target.value })}
                  className="input-empire min-h-[70px]"
                      placeholder="Any special requirements..."
                    />
                  </div>

                    <Button
                      type="submit"
                      disabled={submitting}
                className="w-full btn-empire h-12 text-base font-semibold mt-4"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Booking...
                        </>
                      ) : (
                        "Confirm Booking"
                      )}
                    </Button>
                </form>
          </motion.div>
        )}

        {/* Step 3 — Confirmation */}
        {step === 3 && bookingResult && (
          <motion.div
            key="success"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="container-empire max-w-md py-8"
          >
            <div className="text-center">
              {/* Animated check */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", damping: 15, stiffness: 200 }}
                className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-5 ring-4 ring-green-500/30"
              >
                <CheckCircle className="w-10 h-10 text-green-500" />
              </motion.div>

              <h2 className="text-2xl font-bold text-white mb-2">Booking Submitted!</h2>
              <p className="text-neutral-400 text-sm mb-6">
                Your studio booking request has been received.
              </p>

              {/* Booking ref */}
              <div className="text-xl font-mono font-bold text-empire-gold mb-6">
                {bookingResult.booking_number}
              </div>

              {/* Summary */}
              <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-5 text-left mb-6">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Date</span>
                    <span className="text-white">{bookingResult.booking_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Time</span>
                    <span className="text-white">{bookingResult.start_time} – {bookingResult.end_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Duration</span>
                    <span className="text-white">{selectedDuration} hr{selectedDuration > 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-neutral-800">
                    <span className="text-neutral-500">Total</span>
                    <span className="text-empire-gold font-bold">
                      KES {bookingResult.total_amount?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* What happens next */}
              <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-5 text-left mb-6">
                <h3 className="text-white font-semibold text-sm mb-3">What happens next</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-empire-gold flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-400">
                      Confirmation email sent to <span className="text-white">{guestInfo.guest_email}</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-empire-gold flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-400">
                      Payment settled on arrival or via M-Pesa before your session
                    </span>
                  </li>
                </ul>
              </div>

              <Button onClick={resetBooking} className="btn-empire h-11 px-6">
                Book Another Slot
              </Button>
        </div>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};
